import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { InsightDrillTarget, ServerApiClient } from '../../../../data/ServerApiClient'
import { useAnalytics } from '../../../../analytics'
import { CfoInteriorPage } from '../CfoInteriorPage'
import { CfoCardSkeletons } from '../CfoPagePrimitives'
import type { CfoNavProfile } from '../cfoNav'
import type { CfoRuntimeProfile } from '../cfoRuntimeProfile'
import { CFO_EVENTS, CFO_SURFACES } from '../cfoTelemetry'
import {
  defaultBasis,
  firstPresentLens,
  footerEvidenceLine,
  narrativeFallback,
} from './ceoCockpitModel'
import { BASIS_LABEL, PERIOD_LABEL } from './ceoFormat'
import {
  ceoCockpitUrlStateEquals,
  readCeoCockpitUrlState,
  writeCeoCockpitUrlState,
  type CeoCockpitUrlState,
  type CeoExploreSection,
} from './ceoCockpitUrlState'
import type {
  CeoBasisKind,
  CeoCockpitResponse,
  CeoDecision,
  CeoDimensionKind,
  CeoMetricId,
  CeoPeriodKind,
} from './ceoCockpitTypes'
import { useCeoCockpit, useCeoCockpitSnapshots, type CeoCockpitLoadStatus } from './useCeoCockpit'
import { CeoCadenceView } from './panels/CeoCadenceView'
import { CeoNavStrip } from './panels/CeoNavStrip'
import { CeoSetupPanel } from './panels/CeoSetupPanel'
import type { CeoIncidentAction } from './panels/CeoIncidentCard'
import type { CeoCadence, CeoCadenceItem } from './ceoCockpitTypes'
import type { CeoSurface } from './ceoCockpitUrlState'
import { CeoErrorBoundary } from './CeoErrorBoundary'
import { CeoCashStrip } from './panels/CeoCashStrip'
import { CeoCustomersPanel } from './panels/CeoCustomersPanel'
import { CeoDecisionRail } from './panels/CeoDecisionRail'
import { CeoDimensionPanel } from './panels/CeoDimensionPanel'
import { CeoInventoryPanel } from './panels/CeoInventoryPanel'
import { CeoLaborPanel } from './panels/CeoLaborPanel'
import { CeoMasthead } from './panels/CeoMasthead'
import { CeoPacePanel } from './panels/CeoPacePanel'
import { CeoSaasPanel } from './panels/CeoSaasPanel'
import { CeoScorecard } from './panels/CeoScorecard'
import { CeoExploreFrame } from './panels/CeoExploreFrame'
import { buildCeoIncidentAnalysisRequest } from './ceoIncidentAnalysis'
import './CfoCeoCockpitPage.css'

const SURFACE = CFO_SURFACES.ceo

const PERIOD_OPTIONS: Array<{ id: CeoPeriodKind; label: string }> = [
  { id: 'mtd', label: PERIOD_LABEL.mtd },
  { id: 'qtd', label: PERIOD_LABEL.qtd },
  { id: 'ytd', label: PERIOD_LABEL.ytd },
]

const BASIS_OPTIONS: Array<{ id: CeoBasisKind; label: string }> = [
  { id: 'plan', label: 'vs plan' },
  { id: 'forecast', label: 'vs forecast' },
  { id: 'prior_year', label: 'vs prior year' },
  { id: 'prior_period', label: 'vs prior period' },
]

function displaySnapshotDate(iso: string | null | undefined): string {
  if (!iso) return 'latest'
  const parsed = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function previousMonthEnd(iso: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!match) return null
  const year = Number(match[1])
  const monthIndex = Number(match[2]) - 1
  const parsed = new Date(Date.UTC(year, monthIndex, 1))
  parsed.setUTCDate(0)
  return parsed.toISOString().slice(0, 10)
}

export type CfoCeoCockpitPageProps = {
  appApi: ServerApiClient
  workbookId: string | null
  onNavSelect: (navId: string) => void
  onOpenCfoCore?: (target?: InsightDrillTarget | null) => void
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
  /** Test / harness override. When set, the live API is not called. */
  fixture?: CeoCockpitResponse | null
  fixtureStatus?: CeoCockpitLoadStatus
  fixtureError?: string | null
  onReload?: () => void
}

export function CfoCeoCockpitPage({
  appApi,
  workbookId,
  onNavSelect,
  onOpenCfoCore,
  navProfile,
  runtimeProfile,
  fixture,
  fixtureStatus,
  fixtureError,
  onReload,
}: CfoCeoCockpitPageProps) {
  const [urlState, setUrlState] = useState<CeoCockpitUrlState>(() =>
    readCeoCockpitUrlState(typeof window === 'undefined' ? null : window.location, workbookId),
  )
  const selectedSnapshotDate = urlState.date
  const historical = Boolean(selectedSnapshotDate)
  const [retainedMode, setRetainedMode] = useState(false)
  const live = useCeoCockpit(appApi, fixture ? null : workbookId, selectedSnapshotDate, retainedMode)
  const history = useCeoCockpitSnapshots(appApi, fixture ? null : workbookId)
  const payload = fixture ?? live.payload
  const status: CeoCockpitLoadStatus = fixture ? (fixtureStatus || 'ready') : live.status
  const error = fixture ? fixtureError || null : live.error
  const reload = onReload || live.reload
  const reconstructed = historical && payload?.status.review_mode === 'current_books_as_of'
  const analytics = useAnalytics()
  const latestSnapshotDate = history.index?.latest_date || payload?.as_of_date || ''
  const previousMonthEndDate = previousMonthEnd(latestSnapshotDate)
  const [reviewDateInput, setReviewDateInput] = useState(selectedSnapshotDate || latestSnapshotDate)
  const [snapshotDateMessage, setSnapshotDateMessage] = useState<string | null>(null)

  useEffect(() => {
    setReviewDateInput(selectedSnapshotDate || latestSnapshotDate)
    setSnapshotDateMessage(null)
  }, [selectedSnapshotDate, latestSnapshotDate])

  useEffect(() => {
    const sync = () => {
      const next = readCeoCockpitUrlState(
        typeof window === 'undefined' ? null : window.location,
        workbookId,
      )
      setUrlState((prev) => (ceoCockpitUrlStateEquals(prev, next) ? prev : next))
    }
    sync()
    // The URL is writable by more than this page: the guided tour names a
    // surface via pushState + fg_route_change, and the back button rewinds it.
    // What the URL says must win over what this component last committed —
    // the file's own rule ("a link may name a surface") applied to links that
    // arrive while the page is already mounted. `commit` uses replaceState and
    // never dispatches fg_route_change, so this cannot loop.
    window.addEventListener('fg_route_change', sync)
    window.addEventListener('popstate', sync)
    return () => {
      window.removeEventListener('fg_route_change', sync)
      window.removeEventListener('popstate', sync)
    }
  }, [workbookId])

  const period = urlState.period
  const basis = payload ? defaultBasis(payload.basis_availability, urlState.basis) : urlState.basis
  const lens = urlState.lens
  const metric = urlState.metric
  const focus = urlState.focus

  const commit = (patch: Partial<CeoCockpitUrlState>) => {
    setUrlState((prev) => {
      const next = { ...prev, ...patch }
      writeCeoCockpitUrlState(next, workbookId)
      return next
    })
  }

  useEffect(() => {
    if (status !== 'ready' || !payload) return
    analytics.track({
      event: CFO_EVENTS.ceoCockpitLoaded,
      surface: SURFACE,
      props: {
        workbook_id: workbookId,
        industry_key: payload.company?.industry_key ?? null,
        as_of_date: payload.as_of_date,
        attention_count: payload.attention_count,
      },
    })
  }, [analytics, payload, status, workbookId])

  const suggestedPrompts = useMemo(() => {
    if (!payload) {
      return [
        'What needs a decision this morning?',
        'How is sales pacing versus plan?',
        'What is not tracked yet in these books?',
      ]
    }
    // Page-level derivations run OUTSIDE the section boundaries, so they must
    // never dereference an optional/absent contract field.
    const top = Array.isArray(payload.decisions) ? payload.decisions[0] : undefined
    const company = payload.company?.name || 'this company'
    if (historical) {
      return [
        top
          ? `What drove this decision as of ${selectedSnapshotDate}: ${top.headline}`
          : `What needed a decision at ${company} as of ${selectedSnapshotDate}?`,
        `How was ${company} pacing versus ${BASIS_LABEL[basis]} in this ${reconstructed ? 'as-of review' : 'snapshot'}?`,
        reconstructed
          ? `What currently available book data supports the ${selectedSnapshotDate} review?`
          : `What evidence was retained in the ${selectedSnapshotDate} snapshot?`,
      ]
    }
    return [
      top ? `Why does ${company} need to decide: ${top.headline}` : `What needs a decision this morning at ${company}?`,
      `How is ${company} pacing versus ${BASIS_LABEL[basis]}?`,
      narrativeFallback(payload),
    ]
  }, [payload, basis, historical, reconstructed, selectedSnapshotDate])

  const surface = urlState.surface
  const exploreSection = urlState.section
  const periodOptions = PERIOD_OPTIONS
  const comparisonOptions = BASIS_OPTIONS.map((option) => {
    const flag = payload?.basis_availability?.[option.id]
    return {
      id: option.id,
      label: option.label,
      disabled: flag?.available === false,
      hint: flag?.hint || undefined,
    }
  })

  const asOfLabel = payload
    ? `${historical ? (reconstructed ? 'Current books · Through' : 'Retained snapshot · Through') : 'Through'} ${payload.as_of_date}`
    : status === 'not_ready'
      ? 'Preparing…'
      : 'CEO Cockpit'

  const setPeriod = (value: string) => {
    const next = value as CeoPeriodKind
    commit({ period: next })
    analytics.track({
      event: CFO_EVENTS.ceoPeriodChange,
      surface: SURFACE,
      props: { period: next },
    })
  }
  const setBasis = (value: string) => {
    const next = value as CeoBasisKind
    commit({ basis: next })
    analytics.track({
      event: CFO_EVENTS.ceoBasisChange,
      surface: SURFACE,
      props: { basis: next },
    })
  }
  const setLens = (next: CeoDimensionKind) => {
    // A new lens invalidates the focused row (row keys are per-dimension).
    commit({ lens: next, focus: null })
    analytics.track({
      event: CFO_EVENTS.ceoLensChange,
      surface: SURFACE,
      props: { lens: next },
    })
  }
  const setFocus = (next: string | null) => {
    commit({ focus: next })
  }
  /**
   * Acknowledge, snooze, assign or resolve.
   *
   * The optimistic path is deliberately absent: a CEO who taps Acknowledge and
   * sees the card fade, only for it to return tomorrow because the write
   * failed, has been told a lie about their own action. The page reloads from
   * the server so what it shows is what was recorded.
   */
  const handleIncidentAction = async (action: CeoIncidentAction, item: CeoCadenceItem) => {
    if (historical) return
    analytics.track({
      event: CFO_EVENTS.ceoDecisionDrill,
      surface: SURFACE,
      props: { id: item.key, rule: item.family, kind: action },
    })
    if (!workbookId || fixture) return
    try {
      await appApi.postQuickBooksCeoIncidentAction(workbookId, item.key, action)
      reload()
    } catch (err) {
      analytics.track({
        event: CFO_EVENTS.ceoSectionError,
        surface: SURFACE,
        props: {
          section: 'incident action',
          message: String((err as Error)?.message || err).slice(0, 200),
          workbook_id: workbookId,
        },
      })
    }
  }
  const setSurface = (next: CeoSurface) => {
    commit({ surface: next })
    analytics.track({
      event: CFO_EVENTS.ceoPanelOpen,
      surface: SURFACE,
      props: { panel: next },
    })
  }
  /** A callout chip switches horizon and scrolls to the card it names. */
  const jumpToItem = (cadence: CeoCadence, key: string) => {
    commit({ surface: cadence, focus: key })
    if (typeof window === 'undefined') return
    window.requestAnimationFrame(() => {
      const el = document.getElementById(`ceo-inc-${key}`)
      if (!el) return
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } catch {
        el.scrollIntoView()
      }
    })
  }
  const setMetric = (next: CeoMetricId) => {
    const section: CeoExploreSection = next === 'sales' || next === 'revenue'
      ? 'sales'
      : next === 'gross_profit' || next === 'net_income'
        ? 'profit'
        : next === 'cash'
          ? 'cash'
          : next === 'accounts_receivable'
            ? 'customers'
            : next === 'mrr'
              ? 'subscriptions'
              : exploreSection
    commit({ metric: next, section })
  }
  const setExploreSection = (section: CeoExploreSection) => commit({ section })
  const reportSectionError = (error: Error, info: { section: string }) => {
    analytics.track({
      event: CFO_EVENTS.ceoSectionError,
      surface: SURFACE,
      props: { section: info.section, message: String(error?.message || error).slice(0, 200), workbook_id: workbookId },
    })
  }

  const ask = (
    prompt: string,
    copilot: { sendMessage: (message: string, overrides?: Record<string, unknown>) => void },
    overrides?: Record<string, unknown>,
  ) => {
    const scopedPrompt = payload?.current_source
      ? `Review native monthly statements through ${payload.current_source.period_end}. Daily/customer/incident facts are unavailable, and native agreement is not GL or Close proof. ${prompt}`
      : historical
      ? reconstructed
        ? `Review the CEO Cockpit reconstructed from the currently available books through ${selectedSnapshotDate}. Do not use transactions after that date. ${prompt}`
        : `Review the retained CEO Cockpit snapshot as of ${selectedSnapshotDate}. Do not use later data. ${prompt}`
      : prompt
    copilot.sendMessage(scopedPrompt, overrides)
  }

  const setSnapshotDate = (date: string | null) => { setRetainedMode(false); commit({ date, focus: null }) }
  const reviewSnapshotDate = (date: string) => {
    setReviewDateInput(date)
    if (!date) {
      setSnapshotDateMessage(null)
      setSnapshotDate(null)
      return
    }
    if (date === latestSnapshotDate) {
      setSnapshotDateMessage(null)
      setSnapshotDate(null)
      return
    }
    setSnapshotDateMessage(`Preparing ${displaySnapshotDate(date)} from the books available now…`)
    setSnapshotDate(date)
  }

  return (
    <CfoInteriorPage
      appApi={appApi}
      workbookId={workbookId}
      navId="ceo"
      activeSection={SURFACE}
      eyebrow="CEO"
      title={payload?.current_source ? 'Native monthly review' : 'This morning'}
      hidePageHead
      crumb={
        <>
          <b>CEO</b> · {payload?.current_source ? `Native through ${displaySnapshotDate(payload.current_source.period_end)}` : historical ? `As of ${displaySnapshotDate(selectedSnapshotDate)}` : 'This morning'}
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={period}
      onPeriodChange={setPeriod}
      periodAriaLabel="Select period"
      comparisonLabel={BASIS_OPTIONS.find((option) => option.id === basis)?.label || 'vs plan'}
      comparisonOptions={comparisonOptions}
      comparisonValue={basis}
      onComparisonChange={setBasis}
      comparisonAriaLabel="Select comparison basis"
      pageLoading={status === 'loading' || status === 'not_ready'}
      pageLoadingLabel={
        status === 'not_ready'
          ? live.periodRun?.message || 'The selected period is being prepared…'
          : historical
            ? `Loading ${displaySnapshotDate(selectedSnapshotDate)} from the current books…`
            : 'Loading this morning’s cockpit…'
      }
      copilotSubtitle={payload?.current_source ? `reading native monthly statements through ${payload.current_source.period_end}` : historical ? (reconstructed ? `reading the books through ${displaySnapshotDate(selectedSnapshotDate)}` : `reading the ${displaySnapshotDate(selectedSnapshotDate)} snapshot`) : 'reading this morning’s cockpit'}
      copilotPlaceholder={payload?.current_source ? 'Ask about the native monthly statements…' : historical ? (reconstructed ? 'Ask about this as-of review…' : 'Ask about this retained snapshot…') : 'Ask about this morning…'}
      copilotIntro={payload?.current_source ? `You are reading native monthly statements through ${payload.current_source.period_end}. Daily, customer and incident checks are unavailable; native report agreement is not GL or Close verification.` : historical
        ? reconstructed
          ? `You’re reviewing a CEO Cockpit reconstructed from the currently available books through ${displaySnapshotDate(selectedSnapshotDate)}. Answers should not use transactions after that date.`
          : `You’re reviewing the retained CEO Cockpit snapshot from ${displaySnapshotDate(selectedSnapshotDate)}. Answers should use only evidence retained in this snapshot.`
        : 'You’re on the CEO cockpit. Ask what needs a decision, why a number moved, or how to start tracking a missing dimension.'}
      askPlaceholder={payload?.current_source ? 'Ask about the native monthly statements…' : historical ? (reconstructed ? 'Ask about this as-of review…' : 'Ask about this retained snapshot…') : 'Ask about this morning…'}
      suggestedPrompts={suggestedPrompts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {({ copilot }) => {
        if (status === 'error' && !payload) {
          return (
            <div className="ceo-page" data-testid="ceo-cockpit-error">
              <p className="ceo-empty">
                {error
                    ? `The CEO cockpit could not load. ${error}`
                    : 'The CEO cockpit could not load.'}
              </p>
              <button type="button" className="ceo-text-btn" onClick={historical ? () => setSnapshotDate(null) : reload}>
                {historical ? 'Back to latest' : 'Retry'}
              </button>
              {historical && !history.index?.current_source && !fixture ? <button type="button" className="ceo-text-btn" onClick={live.reconstructPeriod}>Reconstruct selected period</button> : null}
            </div>
          )
        }
        if ((status === 'loading' || status === 'not_ready') && !payload) {
          return (
            <div className="ceo-page" data-testid="ceo-cockpit-not-ready">
              <p className="ceo-kicker">CEO cockpit</p>
              <h1 className="ceo-morning-line">
                {historical
                  ? live.periodRun?.message || 'The selected period is being reconstructed.'
                  : 'This morning’s numbers are being prepared.'}
              </h1>
              {historical && live.periodRun ? (
                <div className="ceo-period-run-progress" role="status" aria-live="polite">
                  <progress max={100} value={live.periodRun.progress_pct} />
                  <span>{live.periodRun.progress_pct}% · using books available through {displaySnapshotDate(live.periodRun.source_data_through)}</span>
                </div>
              ) : null}
              <CfoCardSkeletons count={4} label={historical ? 'Preparing decisions for this period…' : 'Loading this morning’s decisions…'} />
            </div>
          )
        }
        if (!payload) {
          return (
            <div className="ceo-page" data-testid="ceo-cockpit-empty">
              <p className="ceo-empty">CEO Cockpit is not prepared.</p>
            </div>
          )
        }
        if (payload.current_source) {
          const source = payload.current_source
          return (
            <div className="ceo-page" data-testid="ceo-current-native">
              <h2>{source.completeness === 'partial_month' ? 'Month to date' : historical ? 'Native complete month' : 'Latest closed month'} · {displaySnapshotDate(source.period_end)}</h2>
              <p role="status">Current {source.accounting_basis} native monthly statements through {source.period_end}. Native report agreement does not verify the general ledger or close the books.</p>
              <label>Native period end
                <select aria-label="Native CEO period end" value={selectedSnapshotDate || source.period_end} onChange={(event) => setSnapshotDate(event.target.value)}>
                  {(history.index?.current_source?.available_period_ends || [source.period_end]).map((date) => <option key={date} value={date}>{displaySnapshotDate(date)}</option>)}
                </select>
              </label>
              <button type="button" onClick={() => setSnapshotDate(null)}>{source.completeness === 'partial_month' ? 'Latest native period' : 'Latest closed month'}</button>
              {(history.index?.snapshots.length ?? 0) > 0 ? <details><summary>Retained original snapshots</summary>
                {history.index?.snapshots.map((snapshot) => <button type="button" key={snapshot.snapshot_id}
                  onClick={() => {setRetainedMode(true);commit({date:snapshot.as_of_date,focus:null})}}>Original snapshot · {displaySnapshotDate(snapshot.as_of_date)}</button>)}
              </details> : null}
              <CeoScorecard payload={payload} period={period} basis="prior_year" />
              <CeoCashStrip payload={payload} />
              <CeoCustomersPanel payload={payload} />
              <details><summary>Saved operating inputs</summary><CeoSetupPanel payload={payload} appApi={appApi} workbookId={workbookId} readOnly={historical || !!payload.company.is_demo} onSaved={reload} /></details>
              <p className="ceo-muted" data-testid="ceo-native-unavailable">Daily pace, weekly operating facts, incidents, dated A/R, planning, forecasts and optional prose are unavailable in this monthly read. No daily risk checks ran.</p>
              {payload.warnings.map((warning) => <p key={warning} className="ceo-muted">{warning}</p>)}
            </div>
          )
        }
        const presentLens = lens || firstPresentLens(payload)?.kind || null
        const explainDecision = (decision: CeoDecision) => {
          analytics.track({
            event: CFO_EVENTS.ceoDecisionDrill,
            surface: SURFACE,
            props: { id: decision.id, rule: decision.rule, kind: decision.kind },
          })
          ask(`Explain this morning’s decision: ${decision.headline}. ${decision.body}`, copilot)
        }
        // Every major section gets its own boundary so one bad payload field
        // degrades to a quiet card instead of white-screening the cockpit.
        // `resetKey` re-mounts the section when a fresh payload arrives.
        const guard = (section: string, node: ReactNode, compact = false) => (
          <CeoErrorBoundary section={section} onError={reportSectionError} onReload={reload} resetKey={payload} compact={compact}>
            {node}
          </CeoErrorBoundary>
        )
        const explorePanels: Partial<Record<CeoExploreSection, ReactNode>> = {
          morning: guard(
            'This morning',
            <CeoDecisionRail
              payload={payload}
              onOpenCfoCore={onOpenCfoCore}
              onNavSelect={onNavSelect}
              onExplain={explainDecision}
              onLensChange={setLens}
            />,
          ),
          scorecard: guard(
            'Scorecard',
            <CeoScorecard
              payload={payload}
              period={period}
              basis={basis}
              onExplainMetric={(metricId) =>
                ask(`Explain the ${metricId.replace(/_/g, ' ')} tile on the CEO cockpit.`, copilot)
              }
            />,
          ),
          sales: guard(
            'Sales pace',
            <CeoPacePanel
              payload={payload}
              period={period}
              basis={basis}
              metric="sales"
              onMetricChange={setMetric}
              onExplain={() => ask('Explain the sales pace curve this morning.', copilot)}
            />,
          ),
          profit: guard(
            'Profit pace',
            <CeoPacePanel
              payload={payload}
              period={period}
              basis={basis}
              metric={metric === 'gross_profit' ? 'gross_profit' : 'net_income'}
              onMetricChange={setMetric}
              onExplain={() => ask('Explain the profit pace curve this morning.', copilot)}
            />,
          ),
          where: guard(
            'Where it’s coming from',
            <CeoDimensionPanel
              payload={payload}
              period={period}
              basis={basis}
              lens={presentLens}
              focus={focus}
              onLensChange={setLens}
              onFocusChange={setFocus}
              onNavSelect={onNavSelect}
              onAsk={(prompt) => ask(prompt, copilot)}
            />,
          ),
          people: guard('People', <CeoLaborPanel payload={payload} period={period} onAsk={(prompt) => ask(prompt, copilot)} />),
          inventory: guard('Inventory', <CeoInventoryPanel payload={payload} onAsk={(prompt) => ask(prompt, copilot)} />),
          subscriptions: guard('Subscriptions', <CeoSaasPanel payload={payload} onAsk={(prompt) => ask(prompt, copilot)} />),
          customers: guard('Customers', <CeoCustomersPanel payload={payload} onAsk={(prompt) => ask(prompt, copilot)} />),
          cash: guard('Cash & runway', <CeoCashStrip payload={payload} />),
        }
        return (
          <CeoErrorBoundary section="CEO cockpit" onError={reportSectionError} onReload={reload} resetKey={payload}>
            <div className="ceo-page" data-testid="ceo-cockpit-page" data-fg-tour="ceo-cockpit-page">
              <div className={`ceo-snapshot-review${historical ? ' is-historical' : ''}`} data-testid="ceo-snapshot-review">
                <label htmlFor="ceo-snapshot-date">Review as of</label>
                <div className="ceo-snapshot-date-control">
                  <input
                    id="ceo-snapshot-date"
                    type="date"
                    list="ceo-retained-snapshot-dates"
                    aria-label="Review CEO snapshot date"
                    value={reviewDateInput}
                    max={history.index?.latest_date || undefined}
                    disabled={!history.index}
                    onChange={(event) => reviewSnapshotDate(event.target.value)}
                  />
                  <datalist id="ceo-retained-snapshot-dates">
                    {(history.index?.snapshots ?? []).map((snapshot) => (
                      <option key={snapshot.snapshot_id} value={snapshot.as_of_date}>
                        {displaySnapshotDate(snapshot.as_of_date)}
                      </option>
                    ))}
                  </datalist>
                  <button
                    type="button"
                    className="ceo-snapshot-shortcut"
                    disabled={!history.index || !previousMonthEndDate}
                    onClick={() => previousMonthEndDate && reviewSnapshotDate(previousMonthEndDate)}
                  >
                    Previous month end
                    {previousMonthEndDate ? <span>{displaySnapshotDate(previousMonthEndDate)}</span> : null}
                  </button>
                </div>
                {historical ? (
                  <>
                    <span className="ceo-snapshot-readonly">{reconstructed ? 'Read-only as-of review' : 'Read-only retained snapshot'}</span>
                    <button type="button" className="ceo-text-btn" onClick={() => setSnapshotDate(null)}>Back to latest</button>
                    {!fixture && !payload.current_source ? <button type="button" className="ceo-text-btn" onClick={live.reconstructPeriod}>Reconstruct selected period</button> : null}
                  </>
                ) : history.error ? (
                  <span className="ceo-snapshot-hint">History is temporarily unavailable.</span>
                ) : (
                  <span className="ceo-snapshot-hint">Select a retained date to review its original snapshot</span>
                )}
                {reconstructed ? (
                  <span className="ceo-snapshot-message is-ready" role="status">
                    Reconstructed from books available through {displaySnapshotDate(payload.status.source_data_through || payload.as_of_date)}
                  </span>
                ) : snapshotDateMessage ? <span className="ceo-snapshot-message" role="status">{snapshotDateMessage}</span> : null}
              </div>
              {guard('Masthead', <CeoMasthead payload={payload} appApi={appApi} historical={historical} />, true)}
              {guard(
                'Cadence',
                <CeoNavStrip
                  payload={payload}
                  cadence={surface}
                  onCadenceChange={setSurface}
                  onJumpToItem={jumpToItem}
                />,
                true,
              )}
              {surface === 'setup' ? (
                guard(
                  'Setup',
                  <CeoSetupPanel
                    payload={payload}
                    appApi={appApi}
                    workbookId={workbookId}
                    readOnly={historical || !!payload.company.is_demo}
                    onSaved={reload}
                  />,
                )
              ) : surface !== 'explore' ? (
                guard(
                  'Cadence view',
                  <CeoCadenceView
                    payload={payload}
                    cadence={surface}
                    focusedKey={focus}
                    readOnly={historical}
                    onAction={handleIncidentAction}
                    onShowNumbers={(item) => {
                      const request = buildCeoIncidentAnalysisRequest({
                        item,
                        payload,
                        incident: payload.incidents?.find((candidate) => candidate.incident_key === item.key) ?? null,
                        basis,
                      })
                      ask(request.prompt, copilot, {
                        selected_metric_id: request.selectedMetricId,
                        workspace_context: request.workspaceContext,
                      })
                    }}
                    onOpenDrill={onOpenCfoCore}
                    onCadenceChange={setSurface}
                  />,
                )
              ) : (
                <CeoExploreFrame
                  payload={payload}
                  period={period}
                  basis={basis}
                  section={exploreSection}
                  onSectionChange={setExploreSection}
                  onLensChange={setLens}
                  onExplainDecision={explainDecision}
                  panels={explorePanels}
                />
              )}
              <p className="ceo-footer" id="ceo-evidence">
                {footerEvidenceLine(payload)}
              </p>
            </div>
          </CeoErrorBoundary>
        )
      }}
    </CfoInteriorPage>
  )
}
